import { get } from 'lodash';
import { formUtils, MOET_CONSTANTS } from 'moet-core';
import { STAKEHOLDER_TYPES } from '../constants';
import {
  getDefaultStakeholders,
  getDefaultStakeholder,
  onBlurStakeholderDataField,
  onChangeStakeholderDataField,
} from './stakeholderUtils';

const { COUNTRIES_WITH_LOOKUP_DATA } = MOET_CONSTANTS;

export const getMinFromRoleConstraintsInProps = ({
  defaultRole = '',
  roleConstraints: { [defaultRole]: { min = 0 } = {} } = {},
}) => min;

export const getMaxFromRoleConstraintsInProps = ({
  defaultRole = '',
  roleConstraints: { [defaultRole]: { max = Number.MAX_SAFE_INTEGER } = {} } = {},
}) => max;

export const initialState = props => {
  const {
    defaultRole,
    defaultType = STAKEHOLDER_TYPES.PERSON,
    forceCountry = false,
    lookupState = {},
    country,
    region,
    currentStakeholdersCount,
    merchant,
  } = props;
  const min = getMinFromRoleConstraintsInProps(props);
  // prefill stakeholders with info from lookup data
  const defaultStakeholders =
    lookupState.stakeholders && COUNTRIES_WITH_LOOKUP_DATA.includes(country)
      ? lookupState.stakeholders
      : props.defaultStakeholders;
  const stakeholderLength = currentStakeholdersCount || defaultStakeholders.length;
  const entityType = merchant?.businessDetails?.entityType;
  const stakeholders = getDefaultStakeholders(
    min,
    defaultType,
    [defaultRole],
    defaultStakeholders,
    { country, region, entityType },
    forceCountry,
    stakeholderLength,
  );

  // everything that will be returned here is considered to be an initial state in the container
  // and will not be updated, so don't return all props here
  return {
    defaultRole,
    defaultType,
    country,
    stakeholders,
    region,
    entityType: merchant?.businessDetails?.entityType,
  };
};

export const onAddStakeholder = (state, props) => event => {
  const { stakeholders, defaultType = STAKEHOLDER_TYPES.PERSON, country, region, merchant } = state;
  const { defaultRole, roleConstraints } = props;

  const entityType = merchant?.businessDetails?.entityType;
  const max = getMaxFromRoleConstraintsInProps({ defaultRole, roleConstraints });
  if (stakeholders.length >= max) {
    return stakeholders;
  }
  return {
    stakeholders: [
      ...stakeholders,
      getDefaultStakeholder(defaultType, [defaultRole], undefined, {
        country,
        region,
        entityType,
      }),
    ],
  };
};

export const onRemoveStakeholder = props => index => {
  const { stakeholders = [] } = props;
  const min = getMinFromRoleConstraintsInProps(props);
  if (stakeholders.length <= min) {
    return stakeholders;
  }
  return {
    stakeholders: [...stakeholders.slice(0, index), ...stakeholders.slice(index + 1)],
  };
};

export const onRemoveAllStakeholders = () => () => {
  // This partially updates the state by setting the stakeholders array to be empty.
  // No need to filter here since we are already filtering all relevant stakeholders initially.
  return {
    stakeholders: [],
  };
};

export const onChangeRoles = ({ stakeholders }) => (index, role, val) => {
  const newRoles = val
    ? [...stakeholders[index].roles, role]
    : stakeholders[index].roles.filter(val => {
        return val !== role;
      });

  return {
    stakeholders: [
      ...stakeholders.slice(0, index),
      { ...stakeholders[index], roles: newRoles },
      ...stakeholders.slice(index + 1),
    ],
  };
};

export const onChangeStakeholder = props => (index, key, value) => ({
  stakeholders: onChangeStakeholderDataField(index, key, value, props),
});

export const onBlurStakeholder = props => (index, key) => ({
  stakeholders: onBlurStakeholderDataField(index, key, props),
});

export const onNextWrapper = (originalOnNext, stakeholders, onChangeStakeholderFn) => (
  body,
  customValidation = undefined,
  { extraFields, onNext = originalOnNext } = {},
) => {
  const mapKeys = (data, rootKey = '') =>
    Object.keys(data)
      .map(k =>
        data[k].value !== undefined ? `${rootKey}${k}` : mapKeys(data[k], `${rootKey}${k}.`).flat(),
      )
      .flat();

  // For stakeholders, we don't use the initializeFields, we call onChange on every field, simply
  // because we don't have access to the state
  stakeholders.forEach((stakeholder, sIndex) => {
    mapKeys(stakeholder.data).forEach(k => {
      onChangeStakeholderFn(sIndex, k, get(stakeholder.data, `${k}.value`));
    });
  });

  // Flatten the address fields into a single fields object
  const fieldsArray = stakeholders.map(({ data }) =>
    Object.keys(data).reduce(
      (acc, key) => ({
        ...acc,
        ...(key === 'address' ? data[key] : { [key]: data[key] }),
      }),
      {},
    ),
  );

  return formUtils.callToActionOnNextWrapper(
    extraFields !== undefined ? fieldsArray.concat(extraFields) : fieldsArray,
    () => onNext(body),
    customValidation,
  );
};

export const onTouchPersonalId = ({ stakeholders }) => index => {
  return {
    stakeholders: [
      ...stakeholders.slice(0, index),
      {
        ...stakeholders[index],
        data: {
          ...stakeholders[index].data,
          isPersonalIdPristine: {
            ...stakeholders[index].data.isPersonalIdPristine,
            value: false,
          },
        },
      },
      ...stakeholders.slice(index + 1),
    ],
  };
};
